import { useState, useRef, useCallback } from "react";
import {
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  Copy,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  useUploadDocument,
  type UploadResult,
} from "../hooks/useUploadDocument";

interface UploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
  }).format(value);
}

const DTE_TYPE_LABELS: Record<string, string> = {
  "33": "Factura Electrónica",
  "34": "Factura Exenta",
  "39": "Boleta Electrónica",
  "41": "Boleta Exenta",
  "52": "Guía de Despacho",
  "56": "Nota de Débito",
  "61": "Nota de Crédito",
};

export function UploadDialog({
  open,
  onOpenChange,
  onSuccess,
}: UploadDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { upload, isUploading, result, error, reset } = useUploadDocument();

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.name.endsWith(".xml")) {
        setSelectedFile(file);
      }
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      await upload(selectedFile);
    } catch {
      // Error handled by hook
    }
  };

  const handleClose = () => {
    if (result && result.documentsStored > 0) {
      onSuccess();
    }
    setSelectedFile(null);
    reset();
    onOpenChange(false);
  };

  const handleReset = () => {
    setSelectedFile(null);
    reset();
  };

  // Determine view state
  const showDropzone = !selectedFile && !result && !error;
  const showFileSelected = selectedFile && !isUploading && !result && !error;
  const showUploading = isUploading;
  const showResult = result && !isUploading;
  const showError = error && !isUploading && !result;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Subir XML de Documento
          </DialogTitle>
          {!result && !error && (
            <DialogDescription>
              Sube un archivo XML de DTE recibido de un proveedor (EnvioDTE).
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Drop zone */}
        {showDropzone && (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground mb-2">
              Arrastra un archivo XML aquí o
            </p>
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
            >
              Seleccionar archivo
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xml"
              className="hidden"
              onChange={handleFileSelect}
            />
            <p className="text-xs text-muted-foreground mt-4">
              Solo archivos XML de EnvioDTE
            </p>
          </div>
        )}

        {/* File selected - ready to upload */}
        {showFileSelected && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 border rounded-lg bg-muted/50">
              <FileText className="h-8 w-8 text-primary" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{selectedFile.name}</p>
                <p className="text-sm text-muted-foreground">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={handleReset}>
                Cambiar
              </Button>
            </div>
          </div>
        )}

        {/* Uploading state */}
        {showUploading && (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              Procesando archivo XML...
            </p>
          </div>
        )}

        {/* Error state */}
        {showError && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 border rounded-lg bg-red-50 border-red-200">
              <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Error al procesar</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Result state */}
        {showResult && <ResultView result={result} />}

        <DialogFooter>
          {showDropzone && (
            <Button variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
          )}
          {showFileSelected && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button onClick={handleUpload}>Subir</Button>
            </>
          )}
          {showError && (
            <>
              <Button variant="outline" onClick={handleReset}>
                Intentar de nuevo
              </Button>
              <Button onClick={handleClose}>Cerrar</Button>
            </>
          )}
          {showResult && <Button onClick={handleClose}>Cerrar</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResultView({ result }: { result: UploadResult }) {
  const hasStored = result.documentsStored > 0;
  const hasErrors = result.documents.some((d) => d.status === "error");

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div
        className={`flex items-center gap-4 p-4 rounded-lg ${
          hasStored && !hasErrors
            ? "bg-green-50"
            : hasErrors
              ? "bg-yellow-50"
              : "bg-muted"
        }`}
      >
        {hasStored && !hasErrors ? (
          <CheckCircle className="h-8 w-8 text-green-500" />
        ) : hasErrors ? (
          <AlertTriangle className="h-8 w-8 text-yellow-500" />
        ) : (
          <Copy className="h-8 w-8 text-muted-foreground" />
        )}
        <div>
          <p className="font-medium">
            {hasStored && !hasErrors
              ? "Documentos procesados correctamente"
              : hasErrors
                ? "Algunos documentos tuvieron errores"
                : "No se procesaron nuevos documentos"}
          </p>
          <p className="text-sm text-muted-foreground">
            {result.documentsStored > 0 && (
              <span className="text-green-600">
                {result.documentsStored} nuevo(s)
              </span>
            )}
            {result.documentsStored > 0 &&
              result.duplicatesSkipped > 0 &&
              " | "}
            {result.duplicatesSkipped > 0 && (
              <span>{result.duplicatesSkipped} duplicado(s)</span>
            )}
          </p>
        </div>
      </div>

      {/* Document list */}
      <div className="max-h-64 overflow-y-auto border rounded-lg">
        {result.documents.map((doc, idx) => (
          <div
            key={idx}
            className={`p-3 border-b last:border-b-0 ${
              doc.status === "stored"
                ? "bg-green-50"
                : doc.status === "duplicate"
                  ? "bg-muted/50"
                  : "bg-red-50"
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm">
                  {DTE_TYPE_LABELS[doc.summary.documentTypeCode] ??
                    `Tipo ${doc.summary.documentTypeCode}`}{" "}
                  N° {doc.summary.folio}
                </p>
                <p className="text-sm text-muted-foreground truncate">
                  {doc.summary.issuerName}
                </p>
                <p className="text-xs text-muted-foreground">
                  RUT: {doc.summary.issuerTaxId} • {doc.summary.issueDate}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium">
                  {formatCurrency(doc.summary.totalAmount)}
                </p>
                {doc.status === "stored" && (
                  <span className="text-xs text-green-600 flex items-center gap-1 justify-end">
                    <CheckCircle className="h-3 w-3" /> Guardado
                  </span>
                )}
                {doc.status === "duplicate" && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                    <Copy className="h-3 w-3" /> Duplicado
                  </span>
                )}
                {doc.status === "error" && (
                  <span className="text-xs text-red-600 flex items-center gap-1 justify-end">
                    <XCircle className="h-3 w-3" /> Error
                  </span>
                )}
              </div>
            </div>
            {doc.error && (
              <p className="text-xs text-red-600 mt-1">{doc.error}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
